import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { profilesService } from '@/services/profiles.service';
import { auth } from '@/lib/firebase';
import type { User as FirebaseUser } from 'firebase/auth';
import type { User } from '@/types';

interface OnboardingContextType {
  needsOnboarding: boolean;
  setNeedsOnboarding: (value: boolean) => void;
  profile: User | null;
  setProfile: (profile: User | null) => void;
  checkProfileExists: () => Promise<boolean>;
  loading: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);

  // Listen to Firebase auth state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setFirebaseUser(user);
    });

    return () => unsubscribe();
  }, []);

  // Check profile exists when user logs in
  useEffect(() => {
    const checkProfile = async () => {
      if (!firebaseUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const exists = await checkProfileExists();

        if (!exists) {
          setNeedsOnboarding(true);
          setProfile(null);
        } else {
          setNeedsOnboarding(false);
          // Load profile data
          const profileData = await profilesService.getProfile();
          setProfile(profileData);
        }
      } catch (error) {
        console.error('Error checking profile:', error);
      } finally {
        setLoading(false);
      }
    };

    checkProfile();
  }, [firebaseUser]);

  const checkProfileExists = async (): Promise<boolean> => {
    try {
      const exists = await profilesService.profileExists();
      return exists;
    } catch (error) {
      console.error('Error checking profile exists:', error);
      return false;
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        needsOnboarding,
        setNeedsOnboarding,
        profile,
        setProfile,
        checkProfileExists,
        loading,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
